import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaTachometerAlt, FaUsers, FaCog, FaSignOutAlt, FaBars, FaTimes, FaWallet, FaDna, FaFileContract, FaStar, FaHome } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

const NavLink = ({ to, icon, children, isCollapsed }) => {
    const location = useLocation();
    const isActive = location.pathname === to || (to !== '/partner-dashboard' && location.pathname.startsWith(to));
    return (
        <Link to={to} className={`flex items-center p-3 my-1 rounded-lg transition-colors duration-200 ${isActive ? 'bg-amber-500 text-navy-dark' : 'hover:bg-white/10 text-white'}`}>
            {icon}
            {!isCollapsed && <span className="ml-4 font-semibold">{children}</span>}
        </Link>
    );
};

const PartnerLayout = ({ children }) => {
    const [isCollapsed, setCollapsed] = useState(false);
    const { userData, signOut } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        try {
            if (signOut) {
                await signOut(navigate);
            }
        } catch (error) {
            console.error('Logout error:', error);
            navigate('/login');
        }
    };

    return (
        <div className="flex h-screen bg-navy-dark text-white">
            {/* Sidebar */}
            <aside className={`relative bg-white/5 border-r border-white/10 transition-all duration-300 flex flex-col ${isCollapsed ? 'w-20' : 'w-64'}`}>
                {/* Logo Area */}
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                    {!isCollapsed && (
                        <Link to="/" className="flex items-center space-x-2">
                            <img src="/PettAI_logo.webp" alt="PettAI" className="h-10 w-auto object-contain" />
                            <span className="text-amber-500 text-xs font-semibold uppercase tracking-wider ml-2">Partner</span>
                        </Link>
                    )}
                    {isCollapsed && (
                        <Link to="/" className="mx-auto">
                            <img src="/PettAI_logo.webp" alt="PettAI" className="h-8 w-auto object-contain" />
                        </Link>
                    )}
                    <button onClick={() => setCollapsed(!isCollapsed)} className="p-2 rounded-lg hover:bg-white/10 text-gray-400">
                        {isCollapsed ? <FaBars /> : <FaTimes />}
                    </button>
                </div>

                {/* Navigation */}
                <nav className="p-4 flex-1">
                    <NavLink to="/partner-dashboard" icon={<FaTachometerAlt className="flex-shrink-0" />} isCollapsed={isCollapsed}>Dashboard</NavLink>
                    <NavLink to="/partner-dashboard/billing" icon={<FaWallet className="flex-shrink-0" />} isCollapsed={isCollapsed}>Billing & Earnings</NavLink>

                    {!isCollapsed && <div className="border-t border-white/10 my-4"></div>}

                    <NavLink to="/partner-terms" icon={<FaFileContract className="flex-shrink-0" />} isCollapsed={isCollapsed}>Partner Agreement</NavLink>

                    {!isCollapsed && (
                        <Link to="/" className="flex items-center p-3 my-1 rounded-lg hover:bg-white/10 text-gray-400 mt-2">
                            <FaHome className="flex-shrink-0" />
                            <span className="ml-4 font-semibold">Back to Home</span>
                        </Link>
                    )}
                </nav>

                {/* User Info + Logout at Bottom */}
                <div className="p-4 border-t border-white/10">
                    {!isCollapsed && (
                        <div className="mb-3">
                            <p className="text-white text-sm font-semibold">{userData?.name || 'Partner'}</p>
                            <p className="text-gray-400 text-xs">{userData?.email || ''}</p>
                        </div>
                    )}
                    <button
                        onClick={handleLogout}
                        className={`flex items-center w-full p-3 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors ${isCollapsed ? 'justify-center' : ''}`}
                    >
                        <FaSignOutAlt className="flex-shrink-0" />
                        {!isCollapsed && <span className="ml-4 font-semibold">Logout</span>}
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                <main className="flex-1 overflow-x-hidden overflow-y-auto bg-navy-dark p-6 md:p-8">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default PartnerLayout;